import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  PaperAirplaneIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';
import { renderContentWithMentions } from '../../utils/mentions';

const MentionModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  placeholder = "Write something...",
  title = "Write a comment",
  submitButtonText = "Post",
  initialValue = "",
  maxLength = 2000
}) => {
  const [value, setValue] = useState(initialValue);
  const [previewMode, setPreviewMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset value when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setValue(initialValue);
      setPreviewMode(false);
    }
  }, [isOpen, initialValue]);

  const handleSubmit = async () => {
    if (!value.trim() || submitting) return;
    
    setSubmitting(true);
    try {
      await onSubmit(value.trim());
      setValue('');
      onClose();
    } catch (error) {
      console.error('Error submitting:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatPreviewContent = (content) => {
    return renderContentWithMentions(content, 'text-gray-700 text-sm leading-relaxed');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                previewMode 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <EyeIcon className="h-4 w-4 mr-1.5 inline" />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto min-h-0">
          {previewMode ? (
            /* Preview Mode */
            <div className="bg-gray-50 rounded-lg p-4 min-h-[200px]">
              <div className="text-gray-700 text-sm leading-relaxed">
                {value ? formatPreviewContent(value) : (
                  <p className="text-gray-400 italic">Your content will appear here...</p>
                )}
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <div className="relative">
              <RichTextEditor
                value={value}
                onChange={setValue}
                placeholder={placeholder}
                maxLength={maxLength}
                showMentionButton={false}
                className="min-h-[200px]"
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="text-sm text-gray-500">
            {previewMode ? (
              'Preview mode - switch back to edit to make changes'
            ) : (
              'Use rich formatting and @ to mention users or departments'
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || previewMode || !value.trim()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Posting...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                  {submitButtonText}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentionModal; 